const LICENSE_STORAGE_KEY = 'pos_license_status';
const LICENSE_CHECK_INTERVAL = 24 * 60 * 60 * 1000; // 24 hours
const OFFLINE_GRACE_PERIOD = 7 * 24 * 60 * 60 * 1000; // 7 days

class LicenseService {
    constructor() {
        this.licenseKey = process.env.REACT_APP_LICENSE_KEY;
        this.serverUrl = process.env.REACT_APP_LICENSE_SERVER_URL;
        this.apiKey = process.env.REACT_APP_LICENSE_API_KEY;
    }

    async verifyLicense() {
        // If no license configuration, skip verification (development mode)
        if (!this.licenseKey || !this.serverUrl || !this.apiKey) {
            console.warn('License configuration missing. Running in development mode.');
            return {
                licenseStatus: 'development',
                isValid: true,
                message: 'Development mode - no license required'
            };
        }

        try {
            const response = await fetch(`${this.serverUrl}/verify`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-API-Key': this.apiKey
                },
                body: JSON.stringify({
                    licenseKey: this.licenseKey,
                    posInstanceId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
                    timestamp: Date.now()
                }),
                timeout: 10000 // 10 second timeout
            });

            if (!response.ok) {
                throw new Error(`License verification failed with status: ${response.status}`);
            }

            const result = await response.json();

            // Cache result with timestamp
            this.cacheLicenseStatus(result.data);

            return result.data;
        } catch (error) {
            console.error('License verification error:', error);

            // Fallback to cached status on network error
            const cached = this.getCachedLicenseStatus();
            if (cached) {
                console.log('Using cached license status due to network error');
                return cached;
            }

            // No cached status available
            return {
                licenseStatus: 'error',
                isValid: false,
                message: 'Unable to verify license. Please check your internet connection and try again.'
            };
        }
    }

    cacheLicenseStatus(status) {
        try {
            const cacheData = {
                ...status,
                cachedAt: Date.now()
            };
            localStorage.setItem(LICENSE_STORAGE_KEY, JSON.stringify(cacheData));
        } catch (error) {
            console.error('Error caching license status:', error);
        }
    }

    getCachedLicenseStatus() {
        try {
            const cached = localStorage.getItem(LICENSE_STORAGE_KEY);
            if (!cached) return null;

            const data = JSON.parse(cached);
            const cacheAge = Date.now() - data.cachedAt;

            // Allow 7-day offline grace period for cached licenses
            if (cacheAge > OFFLINE_GRACE_PERIOD) {
                console.warn('Cached license status expired (>7 days old)');
                return null;
            }

            return data;
        } catch (error) {
            console.error('Error reading cached license status:', error);
            return null;
        }
    }

    isLicenseValid(licenseData) {
        if (!licenseData) return false;
        return licenseData.isValid === true;
    }

    shouldCheckLicense() {
        const cached = this.getCachedLicenseStatus();
        if (!cached) return true;

        const timeSinceCheck = Date.now() - cached.cachedAt;
        return timeSinceCheck > LICENSE_CHECK_INTERVAL;
    }

    clearCache() {
        try {
            localStorage.removeItem(LICENSE_STORAGE_KEY);
        } catch (error) {
            console.error('Error clearing license cache:', error);
        }
    }
}

const licenseService = new LicenseService();
export default licenseService;
